import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Target, CheckCircle, ArrowRight } from "lucide-react";

const ArchitectureService = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Security Architecture</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Design robust security frameworks tailored to your business needs and risk profile.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Comprehensive Security Design</h2>
              <p className="mb-6">
                Our security architecture services help you design and implement comprehensive security frameworks that protect your organisation's critical assets while enabling business objectives. We create tailored solutions that align with your specific requirements and risk tolerance, using industry-leading frameworks and proven design patterns.
              </p>

              <h2 className="text-2xl font-bold mb-4">Architecture Deliverables</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Network diagrams</strong> showing security zones, boundaries, and control points</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Data flow diagrams</strong> mapping sensitive data movement and protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security control matrices</strong> detailing controls across all architecture layers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Implementation guides</strong> with step-by-step deployment instructions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security baselines</strong> and configuration standards for all components</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Runbook documentation</strong> for ongoing operations and maintenance</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Reference Architectures</h2>
              <div className="space-y-3 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Cloud Migration Security</p>
                  <p className="text-muted-foreground text-sm">AWS multi-account architecture, Azure landing zones, GCP organisation policies with built-in security controls</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Hybrid Environment Security</p>
                  <p className="text-muted-foreground text-sm">Secure connectivity between on-premises and cloud, consistent identity management, unified monitoring</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Zero Trust Architecture</p>
                  <p className="text-muted-foreground text-sm">Identity-centric security, microsegmentation, continuous verification, least privilege access</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">Architecture Review Process</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="mb-3"><strong>Comprehensive health checks include:</strong></p>
                <ul className="space-y-2 text-sm">
                  <li>• Assessment of 30+ security controls across 6 domains (Identity, Network, Data, Applications, Compute, Management)</li>
                  <li>• Threat modeling specific to your architecture and business context</li>
                  <li>• Compliance gap analysis against relevant standards (ISO 27001, NIST, CIS)</li>
                  <li>• Performance and scalability considerations alongside security</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Recent Success Story</h2>
              <div className="bg-accent/10 rounded-lg p-6">
                <p className="mb-3"><strong>Financial Services Client - Security Consolidation:</strong></p>
                <p className="text-sm mb-3">Consolidated 15 overlapping security tools into an integrated 5-tool stack while improving security posture</p>
                <ul className="space-y-1 text-sm">
                  <li>• <strong>40% reduction</strong> in operational overhead and tool management complexity</li>
                  <li>• <strong>£180K annual savings</strong> in licensing and operational costs</li>
                  <li>• <strong>65% faster</strong> threat detection and response through improved integration</li>
                  <li>• <strong>Unified visibility</strong> across cloud and on-premises environments</li>
                </ul>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Need expert security architecture guidance?</h3>
              <p className="text-muted-foreground mb-6">
                Let's design a security framework that protects your organisation while enabling your business goals.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ArchitectureService;
